<template>
  <LoaderAnimation />
  <div class="site-content" :class="{ 'site-content--loading': !isInitialLoadComplete }">
    <AgeVerification v-if="showAgeModal && isInitialLoadComplete" @verified="onAgeVerified" />
    <NavbarComponent v-if="!shouldHideNavbar && !showAgeModal"  />
    <BlogNavigation v-if="showBlogNavigation && !showAgeModal" class="mt-5"/>
    <div v-if="!showAgeModal" class="site-shell">
      <main class="site-main">
        <router-view />
      </main>
      <FooterComponent v-if="!shouldHideNavbar" />
    </div>
    <!-- WhatsApp Floating Button -->
    <a :href="whatsappUrl"
     class="whatsapp-float text-decoration-none"
     target="_blank"
     rel="noopener noreferrer"
      v-if="!shouldHideNavbar && !showAgeModal && !isFooterVisible">
    <span class="whatsapp-tooltip">¿Necesitas ayuda?</span>
    <i class="fab fa-whatsapp whatsapp-icon mb-2"></i>
  </a>
  </div>
</template>
<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import NavbarComponent from '@/components/NavbarComponent.vue'
import FooterComponent from '@/components/FooterComponent.vue'
import LoaderAnimation from '@/components/Loader.vue'
import BlogNavigation from '@/components/BlogNavigation.vue'
import AgeVerification from '@/components/AgeVerification.vue'
import { useStore } from 'vuex'
import { isAuthenticated } from '@/services/auth.js'
import { useMaintenanceMonitor } from '@/utils/maintenanceMonitor'
import brand from '@/config/brand'
import { loadSiteContactPublic, siteContactPublicRef } from '@/composables/useSiteContact'
import {
  holdInitialLoad,
  releaseInitialLoad,
  isInitialLoadComplete
} from '@/composables/useInitialLoad'
import { preloadHomeHero } from '@/utils/preloadHomeHero'

const route = useRoute()
const router = useRouter()
const store = useStore()
const isAdmin = ref(localStorage.getItem('isAdmin') === 'true')
const isFooterVisible = ref(false)

let footerObserver = null
let handleScrollOrResize = null

// Estado para mostrar el modal de verificación de edad
const showAgeModal = ref(false)

// Función para chequear si debe mostrarse el modal
const checkAgeVerification = () => {
  // Solo mostrar si NO está logueado y no verificó edad
  if (!isAuthenticated() && localStorage.getItem('ageVerified') !== 'true') {
    showAgeModal.value = true
  } else {
    showAgeModal.value = false
  }
}

// Nueva función para manejar el evento del modal
function onAgeVerified() {
  showAgeModal.value = false
  localStorage.setItem('ageVerified', 'true')
}

const whatsappUrl = computed(() => {
  const c = siteContactPublicRef.value
  const raw =
    c && typeof c === 'object'
      ? (c.contact_whatsapp || c.contact_phone || c.phone || '')
      : ''
  const digits = String(raw || '').replace(/\D/g, '')
  const num =
    digits || String(brand.contactWhatsapp || brand.contactPhone || '').replace(/\D/g, '')
  if (!num) return ''
  const msg = (c && typeof c === 'object' && c.whatsapp_message) || brand.whatsappMessage
  return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`
})

// Check if current route is an admin route or account page when user is admin
const shouldHideNavbar = computed(() => {
  // Ocultar navbar/footer en rutas de blog para todos
  if (route.path.startsWith('/blog')) {
    return true
  }
  // Ocultar navbar/footer en cualquier ruta de /configuracion (panel usuario o admin)
  if (route.path.startsWith('/configuracion')) {
    return true
  }
  if (route.path.startsWith('/mantenimiento')) {
    return true
  }
  return false
})

// Show blog navigation on blog routes
const showBlogNavigation = computed(() => {
  return route.path.startsWith('/blog')
})

// Update isAdmin value when localStorage changes
const updateAdminStatus = () => {
  isAdmin.value = localStorage.getItem('isAdmin') === 'true'
}

const setupFooterObserver = () => {
  if (footerObserver) {
    footerObserver.disconnect()
    footerObserver = null
  }

  if (handleScrollOrResize) {
    window.removeEventListener('scroll', handleScrollOrResize)
    window.removeEventListener('resize', handleScrollOrResize)
    handleScrollOrResize = null
  }

  const footerEl = document.querySelector('footer')

  if (!footerEl) {
    isFooterVisible.value = false
    return
  }

  if ('IntersectionObserver' in window) {
    footerObserver = new IntersectionObserver((entries) => {
      const entry = entries[0]
      isFooterVisible.value = Boolean(entry && entry.isIntersecting && entry.intersectionRatio > 0)
    }, {
      root: null,
      threshold: [0, 0.1]
    })
    footerObserver.observe(footerEl)
    return
  }

  const update = () => {
    const rect = footerEl.getBoundingClientRect()
    isFooterVisible.value = rect.top < window.innerHeight && rect.bottom > 0
  }

  update()
  handleScrollOrResize = () => update()
  window.addEventListener('scroll', handleScrollOrResize, { passive: true })
  window.addEventListener('resize', handleScrollOrResize)
}

let homeHeroPreloadPromise = null

const ensureHomeHeroReady = async (path) => {
  if (path !== '/') {
    releaseInitialLoad('home-hero')
    return
  }

  holdInitialLoad('home-hero')
  try {
    if (!homeHeroPreloadPromise) {
      homeHeroPreloadPromise = preloadHomeHero()
    }
    await homeHeroPreloadPromise
  } catch {
    // fail open: mostrar la página aunque falle el preload
  } finally {
    releaseInitialLoad('home-hero')
  }
}

// Watch for route changes to recheck conditions
watch(() => route.path, async (path) => {
  updateAdminStatus()
  checkAgeVerification()
  await ensureHomeHeroReady(path)
  await nextTick()
  setupFooterObserver()
}, { immediate: true })

watch(showAgeModal, async () => {
  await nextTick()
  setupFooterObserver()
})

onMounted(() => {
  loadSiteContactPublic()

  router.isReady().then(() => {
    releaseInitialLoad('app-init')
  })

  const deferCartHydrate = window.requestIdleCallback
    ? (cb) => window.requestIdleCallback(cb, { timeout: 2000 })
    : (cb) => setTimeout(cb, 150)
  deferCartHydrate(() => {
    store.dispatch('hydrateCartFromServer').catch(() => undefined)
  })

  // Initial check
  updateAdminStatus()
  checkAgeVerification()
  nextTick(() => setupFooterObserver())
  
  // Inicializar verificación de mantenimiento con TTL más largo (5 minutos)
  // para reducir consultas al servidor
  useMaintenanceMonitor(5) // Check every 5 minutes (aumentado de 2 a 5 minutos)
  
  // Listen for storage events (in case user logs in/out in another tab)
  window.addEventListener('storage', () => {
    updateAdminStatus()
    checkAgeVerification()
  })

})

onBeforeUnmount(() => {
  if (footerObserver) {
    footerObserver.disconnect()
    footerObserver = null
  }
  if (handleScrollOrResize) {
    window.removeEventListener('scroll', handleScrollOrResize)
    window.removeEventListener('resize', handleScrollOrResize)
  }
})
</script>

<style>
  @import '@/assets/css/variables.css';
@font-face {
  font-family: 'General-Sans';
  src: url('@/assets/fonts/GeneralSans-Variable.ttf') format('truetype');
  font-display: swap;
}
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: 'General-Sans', sans-serif;
  scroll-behavior: smooth;
}

body {
  background-color: var(--bg-primary);
  color: var(--text-primary);
}

.site-shell {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100svh;
}

.site-main {
  flex: 1 1 auto;
  width: 100%;
  min-height: 0;
  padding: 0;
}

.site-content--loading {
  visibility: hidden;
  pointer-events: none;
}

.container {
  max-width: var(--container-max, 1300px);
}

.bg-dark {
  background-color: var(--black-900) !important;
}

.border-primary {
  border-color: var(--primary-color) !important;
}

.display-2, .display-3, .display-4, .display-5 {
  font-weight: 800;
}

.text-white {
  color: #ffffff !important;
}

.text-gradient-primary {
  background: linear-gradient(160deg, var(--primary-color), var(--primary-dark));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* settings-section (panel /configuracion) usa position:sticky; overflow:hidden lo rompe */
section:not(.settings-section) {
  position: relative;
  overflow: hidden;
}

.settings-section {
  position: relative;
  overflow: visible;
}

.h5 {
  font-size: 1.1rem;
  font-weight: 500;
  color: var(--text-primary);
}

.fs-6 {
  font-size: .8rem;
}

.fs-7 {
  font-size: 0.875rem !important;
}

.btn {
  font-size: .85rem !important;
  padding: 0.5rem 1rem;
  border-radius: var(--btn-radius);
  transition: all var(--transition-base);
}

.btn:hover {
  color: inherit;
}

.hover-opacity {
  transition: 0.3s ease;
}

.hover-opacity:hover {
  opacity: 0.8;
}

/* badges */
.status-badge {
  display: inline-flex;
  padding: 0.4rem 0.8rem;
  border-radius: 2rem;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
}

.btn-outline-primary {
  background-color: transparent;
  color: var(--primary-color);
  border: 2px solid var(--primary-color);
  cursor: pointer;
  transition: all var(--transition-base);
}

.btn-outline-primary.active,
.btn-outline-primary:hover {
  background-color: var(--primary-color);
  color: #ffffff;
  border-color: var(--primary-color);
}

.text-primary {
  color: var(--primary-color) !important;
}

.bg-primary {
  background-color: var(--primary-color) !important;
}

.text-dark {
  color: var(--text-primary) !important;
}

.btn-primary {
  background-color: var(--btn-primary-bg) !important;
  color: var(--btn-primary-text) !important;
  border: none !important;
  padding: .5rem 1.3rem;
}

.btn-primary:hover {
  background-color: var(--btn-primary-hover) !important;
}

button, a {
  font-weight: 400;
  font-size: 1rem;
}

h1, h2, h3, h4, h5, h6 {
  font-weight: 700;
  color: var(--text-primary);
}

h1 { font-size: 2rem; }
h2 { font-size: 1.7rem; }
h3 { font-size: 1.5rem; font-weight: 600; letter-spacing: 0.5px; }
h4 { font-size: 1.25rem; font-weight: 600; }
h5 { font-size: 1rem; font-weight: 500; }

p {
  font-size: 1rem;
  color: var(--text-secondary);
  font-weight: 400;
  line-height: 1.6;
}

b { font-weight: 700; }

@media (max-width: 525px) {
  .display-2 { font-size: 3.5rem !important; }
}

@media (max-width: 400px) {
  .display-2 { font-size: 3rem !important; }
}

/* WhatsApp Floating Button Styles */
.whatsapp-float {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: #25d366;
  color: #ffffff;
  border-radius: 50px;
  text-align: center;
  font-size: 30px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.3);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  transition: all 0.3s;
}

.whatsapp-float:hover {
  background-color: #20ba5a;
  color: white;
}

.whatsapp-icon {
  margin-top: 4px;
}

.whatsapp-tooltip {
  position: absolute;
  right: 60px;
  background-color: #333;
  color: white;
  border-radius: 5px;
  padding: 8px 15px;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  visibility: hidden;
  opacity: 0;
  transition: all 0.3s;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
}

.whatsapp-tooltip::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 100%;
  margin-top: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: transparent transparent transparent #333;
}

.whatsapp-float:hover .whatsapp-tooltip {
  visibility: visible;
  opacity: 1;
}

@media screen and (max-width: 768px) {
  .whatsapp-float {
    width: 50px;
    height: 50px;
    font-size: 25px;
    bottom: 15px;
    right: 15px;
  }
  
  .whatsapp-tooltip {
    display: none;
  }
}
</style>
